// src/pages/hotels/HotelLinkReview.js
// Review every hotel's "Check your hotel" link (used in the quotation PDF) and approve it.

import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Button, Stack, TextField, FormControl, Select, MenuItem, InputLabel, Paper, Chip,
  Table, TableHead, TableBody, TableRow, TableCell, TablePagination, IconButton, Tooltip, Tabs, Tab,
  CircularProgress, InputAdornment,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import UndoIcon from "@mui/icons-material/Undo";
import SearchIcon from "@mui/icons-material/Search";
import MapIcon from "@mui/icons-material/Map";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import RefreshIcon from "@mui/icons-material/Refresh";

import { getAllHotels, updateHotel } from "../../api/hotelsAPI";
import useSnackbar from "../../hooks/useSnackbar";
import usePermissions from "../../hooks/UsePermissions";

const isValidUrl = (value) => /^https?:\/\/\S+$/i.test((value || "").trim());

const mapsSearchUrl = (h) => {
  const q = [h.hotel_name, h.sub_destination, h.destination]
    .map((s) => (s || "").trim())
    .filter((s, i, arr) => s && arr.findIndex((x) => x.toLowerCase() === s.toLowerCase()) === i)
    .join(" ");
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
};

export default function HotelLinkReview() {
  const navigate = useNavigate();
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const getPermission = usePermissions();
  const hasHotelModuleAccess = getPermission("hotel", "view");
  // All authenticated users accessing this review page can review, edit, and approve hotel links
  const canEdit = true;

  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({}); // hotelId -> edited link
  const [savingId, setSavingId] = useState(null);

  const [status, setStatus] = useState("pending");
  const [search, setSearch] = useState("");
  const [destination, setDestination] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const fetchHotels = useCallback(async (isManual = false) => {
    setLoading(true);
    try {
      const res = await getAllHotels();
      const list = Array.isArray(res) ? res : res?.data || [];
      list.sort((a, b) => (a.destination || "").localeCompare(b.destination || "") || (a.hotel_name || "").localeCompare(b.hotel_name || ""));
      setHotels(list);
      setDrafts({});
      if (isManual) {
        showSnackbar("Hotel list refreshed", "success");
      }
    } catch (err) {
      showSnackbar("Failed to load hotels", "error");
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    fetchHotels();
  }, [fetchHotels]);

  const destinations = useMemo(
    () => [...new Set(hotels.map((h) => h.destination).filter(Boolean))].sort(),
    [hotels]
  );

  const counts = useMemo(() => ({
    pending: hotels.filter((h) => !h.hotel_link_approved).length,
    approved: hotels.filter((h) => h.hotel_link_approved).length,
    all: hotels.length,
  }), [hotels]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return hotels.filter((h) => {
      if (status === "pending" && h.hotel_link_approved) return false;
      if (status === "approved" && !h.hotel_link_approved) return false;
      if (destination && h.destination !== destination) return false;
      if (q && ![h.hotel_name, h.sub_destination, h.destination].some((v) => (v || "").toLowerCase().includes(q))) return false;
      return true;
    });
  }, [hotels, status, destination, search]);

  useEffect(() => { setPage(0); }, [status, destination, search]);

  const linkOf = (h) => (drafts[h._id] !== undefined ? drafts[h._id] : h.hotel_link || "");
  const isEdited = (h) => drafts[h._id] !== undefined && drafts[h._id] !== (h.hotel_link || "");

  const saveHotel = async (h, payload, message) => {
    setSavingId(h._id);
    try {
      await updateHotel(h._id, payload);
      setHotels((prev) => prev.map((x) => (x._id === h._id ? { ...x, ...payload } : x)));
      setDrafts((prev) => { const next = { ...prev }; delete next[h._id]; return next; });
      showSnackbar(message, "success");
    } catch (err) {
      showSnackbar(err.response?.data?.message || "Failed to update hotel", "error");
    } finally {
      setSavingId(null);
    }
  };

  const handleApprove = (h) => {
    const link = linkOf(h).trim();
    if (!isValidUrl(link)) {
      showSnackbar("Enter a valid link (https://...) before approving", "warning");
      return;
    }
    saveHotel(h, { hotel_link: link, hotel_link_approved: true, hotel_link_approved_at: new Date().toISOString() }, `Approved: ${h.hotel_name}`);
  };

  // Change the link without approving it (stays / goes back to pending)
  const handleSaveLink = (h) => {
    const link = linkOf(h).trim();
    if (!isValidUrl(link)) {
      showSnackbar("Enter a valid link (https://...)", "warning");
      return;
    }
    saveHotel(h, { hotel_link: link, hotel_link_approved: false, hotel_link_approved_at: null }, `Link updated: ${h.hotel_name}`);
  };

  const resetDraft = (h) => setDrafts((prev) => { const next = { ...prev }; delete next[h._id]; return next; });

  const handleRevoke = (h) => {
    saveHotel(h, { hotel_link_approved: false, hotel_link_approved_at: null }, `Moved back to pending: ${h.hotel_name}`);
  };

  const pageRows = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box p={3} sx={{ background: "#f5f6fa", minHeight: "100vh" }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} gap={2} flexWrap="wrap">
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton
            onClick={() => (hasHotelModuleAccess ? navigate("/hotels") : navigate("/dashboard"))}
            size="small"
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h6" fontWeight={600}>Hotel Link Review</Typography>
            <Typography variant="body2" color="text.secondary">
              Open each link, fix it if it points to the wrong place, then approve. These links power “Check your hotel” in the quotation PDF.
            </Typography>
          </Box>
        </Stack>

        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          onClick={() => fetchHotels(true)}
          disabled={loading}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Refresh
        </Button>
      </Box>

      <Paper sx={{ mb: 2, px: 2, pt: 1, pb: 2 }}>
        <Tabs value={status} onChange={(e, v) => setStatus(v)} sx={{ mb: 2 }}>
          <Tab value="pending" label={`Pending (${counts.pending})`} />
          <Tab value="approved" label={`Approved (${counts.approved})`} />
          <Tab value="all" label={`All (${counts.all})`} />
        </Tabs>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center" flexWrap="wrap">
            <TextField
              size="small"
              placeholder="Search hotel or place"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 260 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Destination</InputLabel>
              <Select label="Destination" value={destination} onChange={(e) => setDestination(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                {destinations.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
          <Tooltip title="Refresh hotel list">
            <span>
              <IconButton onClick={() => fetchHotels(true)} disabled={loading} color="primary" size="small">
                {loading ? <CircularProgress size={18} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Paper>

      <Paper>
        {loading ? (
          <Box display="flex" justifyContent="center" p={6}><CircularProgress /></Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: "28%" }}>Hotel</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Link</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 300 }} align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pageRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 6, color: "text.secondary" }}>
                      {status === "pending" ? "No pending links — everything here is approved." : "No hotels match these filters."}
                    </TableCell>
                  </TableRow>
                )}
                {pageRows.map((h) => {
                  const link = linkOf(h);
                  const edited = isEdited(h);
                  const busy = savingId === h._id;
                  return (
                    <TableRow key={h._id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{h.hotel_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[h.sub_destination, h.destination].filter(Boolean).join(", ")}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <TextField
                            size="small"
                            fullWidth
                            value={link}
                            disabled={!canEdit || busy}
                            error={Boolean(link) && !isValidUrl(link)}
                            onChange={(e) => setDrafts((prev) => ({ ...prev, [h._id]: e.target.value }))}
                            inputProps={{ style: { fontSize: 12 } }}
                          />
                          <Tooltip title="Open this link">
                            <span>
                              <IconButton size="small" disabled={!isValidUrl(link)} onClick={() => window.open(link.trim(), "_blank", "noopener")}>
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Find on Google Maps — copy the hotel's link from there and paste it here">
                            <IconButton size="small" onClick={() => window.open(mapsSearchUrl(h), "_blank", "noopener")}>
                              <MapIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        {edited && canEdit ? (
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                            <Tooltip title="Undo change">
                              <span>
                                <IconButton size="small" disabled={busy} onClick={() => resetDraft(h)}><RestartAltIcon fontSize="small" /></IconButton>
                              </span>
                            </Tooltip>
                            <Button size="small" variant="outlined" disabled={busy} startIcon={<SaveIcon />} onClick={() => handleSaveLink(h)}>
                              Save
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              disabled={busy}
                              startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon />}
                              onClick={() => handleApprove(h)}
                            >
                              Save & Approve
                            </Button>
                          </Stack>
                        ) : h.hotel_link_approved ? (
                          <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                            <Chip size="small" color="success" icon={<CheckCircleIcon />} label="Approved" />
                            {canEdit && (
                              <Tooltip title="Move back to pending">
                                <span>
                                  <IconButton size="small" disabled={busy} onClick={() => handleRevoke(h)}><UndoIcon fontSize="small" /></IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Stack>
                        ) : (
                          canEdit && (
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              disabled={busy}
                              startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon />}
                              onClick={() => handleApprove(h)}
                            >
                              Approve
                            </Button>
                          )
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </>
        )}
      </Paper>

      <SnackbarComponent />
    </Box>
  );
}
